import { Injectable, OnDestroy } from '@angular/core';

import { filter } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import {
  WS_STATUS,
  WsChannel,
  IChatMessage,
  WebSocketService,
  IChatMessageError,
  isChatMessageError,
  IChatIncomingFrame,
  IChatOutgoingMessage,
} from '@cakeiteasy/web-utils';

type ChatChannel = WsChannel<IChatIncomingFrame, IChatOutgoingMessage>;

/**
 * Owns the chat channel's lifecycle and splits its frames by kind.
 *
 * Provided per conversation, never in root: `connect` replaces whatever channel it holds.
 */
@Injectable()
export class ChatSocketService implements OnDestroy {
  public readonly status$ = new BehaviorSubject<WS_STATUS>(WS_STATUS.IDLE);

  public readonly messages$: Observable<IChatMessage>;
  public readonly errors$: Observable<IChatMessageError>;

  // Outlives disconnect() so a later connect() can reuse it; only the channel
  // subscriptions are torn down.
  private readonly _frames$ = new Subject<IChatIncomingFrame>();

  private _channel: ChatChannel = null;
  private _subscriptions: Subscription[] = [];

  constructor(private _webSocket: WebSocketService) {
    this.messages$ = this._frames$.pipe(filter((frame): frame is IChatMessage => !isChatMessageError(frame)));

    this.errors$ = this._frames$.pipe(filter(isChatMessageError));
  }

  ngOnDestroy() {
    this.disconnect();
    this._frames$.complete();
    this.status$.complete();
  }

  public connect(parentOrderId: number, customerKey: string | number) {
    this.disconnect();

    const path = this._getPath(parentOrderId, customerKey);
    this._channel = this._webSocket.connect<IChatIncomingFrame, IChatOutgoingMessage>(path);

    this._subscriptions = [
      this._channel.messages$.subscribe((frame) => this._frames$.next(frame)),
      this._channel.status$.subscribe((status) => this.status$.next(status)),
    ];
  }

  public disconnect() {
    // Closed before unsubscribing so the channel's own IDLE reaches `status$`: the channel is
    // the only thing that reports connection state, and this service just forwards it.
    if (this._channel) {
      this._channel.close();
      this._channel = null;
    }

    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
    this._subscriptions = [];
  }

  /** Returns false when the socket is not open, so the caller can fall back. */
  public send(message: IChatOutgoingMessage): boolean {
    return Boolean(this._channel) && this._channel.send(message);
  }

  private _getPath(parentOrderId: number, customerKey: string | number): string {
    const key = encodeURIComponent(String(customerKey));

    return `orders/${parentOrderId}/chat/?customer_key=${key}`;
  }
}
